// @ts-nocheck
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

export type SidebarMode = 'single' | 'dual';
export type ActiveSidebarContext = 'organization' | 'image' | 'multi' | 'gallery' | 'video' | 'audio' | 'text' | '3d' | 'settings' | 'help' | 'admin';

// Sub-context states for contexts that have internal navigation
export interface SubContextState {
  admin: string | null;
  organization: string | null;
  settings: string | null;
  help: string | null;
}

interface SidebarContextType {
  // Legacy props for backward compatibility
  sidebarMode: SidebarMode;
  showWorkflowSidebar: boolean;
  setSidebarMode: (mode: SidebarMode) => void;
  setShowWorkflowSidebar: (show: boolean) => void;
  toggleWorkflowSidebar: () => void;
  
  // New two-level sidebar props
  activeContext: ActiveSidebarContext;
  setActiveContext: (context: ActiveSidebarContext) => void;
  
  // Context panel collapse
  isContextPanelCollapsed: boolean;
  setContextPanelCollapsed: (collapsed: boolean) => void;
  toggleContextPanel: () => void;
  
  // Sub-context state management
  subContextStates: SubContextState;
  setSubContextState: (context: keyof SubContextState, path: string | null) => void;
  getSubContextState: (context: keyof SubContextState) => string | null;
  setActiveContextWithSubState: (context: ActiveSidebarContext, subPath?: string) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // Legacy state
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('single');
  const [showWorkflowSidebar, setShowWorkflowSidebar] = useState(false);
  
  // New state for two-level sidebar
  const [activeContext, setActiveContext] = useState<ActiveSidebarContext>(() => {
    // Try to restore from localStorage
    const saved = localStorage.getItem('sidebar-active-context');
    return (saved as ActiveSidebarContext) || 'organization';
  });
  
  // Context panel collapse state
  const [isContextPanelCollapsed, setContextPanelCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-context-collapsed');
    return saved === 'true';
  });
  
  // Sub-context states
  const [subContextStates, setSubContextStates] = useState<SubContextState>(() => {
    const saved = localStorage.getItem('sidebar-context-states');
    return saved ? JSON.parse(saved) : {
      admin: null,
      organization: null,
      settings: null,
      help: null
    };
  });

  // Save active context to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-active-context', activeContext);
  }, [activeContext]);
  
  // Save context panel collapse state
  useEffect(() => {
    localStorage.setItem('sidebar-context-collapsed', isContextPanelCollapsed.toString());
  }, [isContextPanelCollapsed]);
  
  // Save sub-context states to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-context-states', JSON.stringify(subContextStates));
  }, [subContextStates]);

  const toggleWorkflowSidebar = () => {
    setShowWorkflowSidebar(!showWorkflowSidebar);
    setSidebarMode(!showWorkflowSidebar ? 'dual' : 'single');
  };
  
  const toggleContextPanel = useCallback(() => {
    setContextPanelCollapsed(prev => !prev);
  }, []);
  
  // Sub-context state management methods
  const setSubContextState = useCallback((context: keyof SubContextState, path: string | null) => {
    setSubContextStates(prev => {
      if (prev[context] === path) {
        return prev; // avoid unnecessary state updates
      }
      return {
        ...prev,
        [context]: path
      };
    });
  }, []);
  
  const getSubContextState = useCallback((context: keyof SubContextState): string | null => {
    return subContextStates[context];
  }, [subContextStates]);
  
  const setActiveContextWithSubState = useCallback((context: ActiveSidebarContext, subPath?: string) => {
    setActiveContext(context);
    if (subPath && context in subContextStates) {
      setSubContextState(context as keyof SubContextState, subPath);
    }
  }, [subContextStates, setSubContextState]);

  const value: SidebarContextType = {
    sidebarMode,
    showWorkflowSidebar,
    setSidebarMode,
    setShowWorkflowSidebar,
    toggleWorkflowSidebar,
    activeContext,
    setActiveContext,
    isContextPanelCollapsed,
    setContextPanelCollapsed,
    toggleContextPanel,
    subContextStates,
    setSubContextState,
    getSubContextState,
    setActiveContextWithSubState
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}